"use client";

import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import type { User } from "firebase/auth";
import type { EmployeePermission, EmployeeRole } from "@/lib/admin-security/employeePolicy";
import { useDeferredReady } from "./analytics/DeferThirdParty";

// The Firebase auth SDK (~130KB of JS plus an auth iframe) loads lazily on
// first user engagement so it stays off the critical rendering path.
function loadFirebaseAuth() {
    return Promise.all([import("firebase/auth"), import("../services/firebase")]).then(
        ([authModule, services]) => ({ authModule, clientAuth: services.clientAuth })
    );
}

export interface AuthUser {
    role?: EmployeeRole | string | undefined;
    permissions?: EmployeePermission[] | undefined;
    team?: string | undefined;
    title?: string | undefined;
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    providerData: Array<{ email?: string | null }>;
}

interface AuthContextValue {
    user: AuthUser | null;
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
    refreshToken: () => Promise<void>;
    signOut: () => Promise<void>;
    uid: string | undefined;
    email: string | null | undefined;
    displayName: string | null | undefined;
    photoURL: string | null | undefined;
    providerData: Array<{ email?: string | null }> | undefined;
}

interface AuthProviderProps {
    children: React.ReactNode;
    initialUser?: AuthUser | null;
    syncWithFirebase?: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toAuthUser(user: User | AuthUser | null | undefined): AuthUser | null {
    if (!user) return null;

    return {
        uid: user.uid,
        role: "role" in user ? user.role : undefined,
        permissions: "permissions" in user ? user.permissions : undefined,
        team: "team" in user ? user.team : undefined,
        title: "title" in user ? user.title : undefined,
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        providerData: ("providerData" in user ? user.providerData : [])?.map((provider) => ({
            email: provider?.email ?? null
        })) ?? []
    };
}

async function clearServerSession() {
    await fetch("/api/logout", {
        method: "POST",
        credentials: "same-origin"
    });
}

export function AuthProvider({
    children,
    initialUser = null,
    syncWithFirebase = true
}: AuthProviderProps) {
    const [user, setUser] = useState<AuthUser | null>(() => toAuthUser(initialUser));
    const [loading, setLoading] = useState<boolean>(syncWithFirebase);
    const [error, setError] = useState<string | null>(null);
    const firebaseReady = useDeferredReady();

    const refreshToken = useCallback(async (): Promise<void> => {
        if (!syncWithFirebase) return;

        try {
            const { clientAuth } = await loadFirebaseAuth();

            if (!clientAuth.currentUser) return;
            await clientAuth.currentUser.getIdToken(true);
        } catch (err) {
            console.error("Token refresh failed:", err);
            setError("Failed to refresh authentication token");
        }
    }, [syncWithFirebase]);

    const signOut = useCallback(async (): Promise<void> => {
        try {
            setLoading(true);
            setError(null);

            const { clientAuth } = await loadFirebaseAuth();

            await clientAuth.signOut().catch(() => undefined);
            await clearServerSession();

            localStorage.removeItem("widgetSettings");
            setUser(null);
        } catch (err) {
            console.error("Sign out failed:", err);
            setError("Failed to sign out");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setUser(toAuthUser(initialUser));
        setLoading(syncWithFirebase);
    }, [initialUser, syncWithFirebase]);

    useEffect(() => {
        if (!syncWithFirebase) {
            setLoading(false);
            return undefined;
        }

        if (!firebaseReady) {
            return undefined;
        }

        let mounted = true;
        let unsubscribe: (() => void) | undefined;

        void loadFirebaseAuth()
            .then(({ authModule, clientAuth }) => {
                if (!mounted) return;

                void authModule.setPersistence(clientAuth, authModule.inMemoryPersistence).catch((err) => {
                    console.error("Failed to set Firebase persistence:", err);
                });

                unsubscribe = authModule.onIdTokenChanged(
                    clientAuth,
                    async (firebaseUser) => {
                        if (!mounted) return;

                        try {
                            setError(null);
                            setUser(toAuthUser(firebaseUser));
                        } catch (err) {
                            console.error("Auth state change error:", err);
                            setError("Authentication error occurred");
                            setUser(null);
                        } finally {
                            if (mounted) {
                                setLoading(false);
                            }
                        }
                    },
                    (authError) => {
                        if (!mounted) return;

                        console.error("Auth state listener error:", authError);
                        setError("Authentication service error");
                        setUser(null);
                        setLoading(false);
                    }
                );
            })
            .catch((err) => {
                console.error("Failed to load Firebase auth:", err);
                if (mounted) {
                    setError("Authentication service error");
                    setLoading(false);
                }
            });

        return () => {
            mounted = false;
            unsubscribe?.();
        };
    }, [syncWithFirebase, firebaseReady]);

    useEffect(() => {
        if (!syncWithFirebase || !user) return;

        const interval = setInterval(() => {
            void refreshToken();
        }, 50 * 60 * 1000);

        return () => clearInterval(interval);
    }, [refreshToken, syncWithFirebase, user]);

    const contextValue = useMemo<AuthContextValue>(() => ({
        user,
        loading,
        error,
        isAuthenticated: !!user,
        refreshToken,
        signOut,
        uid: user?.uid,
        email: user?.email,
        displayName: user?.displayName,
        photoURL: user?.photoURL,
        providerData: user?.providerData
    }), [error, loading, refreshToken, signOut, user]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }

    return context;
};

export const useAuthStatus = (): {
    isAuthenticated: boolean;
    loading: boolean;
    user: AuthUser | null;
} => {
    const { user, loading, isAuthenticated } = useAuth();

    return { isAuthenticated, loading, user };
};
